// --- START OF FILE Movies.ts ---

import Dexie, { Table } from "dexie";
// Make sure this path is correct for your project structure
import Collection from "../models/Movies.json";
import { isApple, isDesktop, isMobile, isSafari } from "../components/global";
import xxhash from "xxhash-wasm";

// The main metadata for a movie.
// A completed movie will have the 'blob' property. In-progress movies will not.
interface Movie {
    id: string; // Composite key: "title-episode"
    time: number; // Last playback position // deprecated

    blob?: Blob; // The final, assembled movie data for completed downloads
    size?: number; // Final size of the assembled file
    totalSize?: number; // The total expected size of the file when complete
    downloaded?: number; // Bytes currently downloaded (used for in-progress downloads)

    status?: "started" | "paused" | "failed" | "completed";
}

// Represents a single chunk of a movie file, used only during the download process.
interface MovieChunk {
    movieId: string; // Foreign key linking to the Movie.id
    order: number; // The sequence number of this chunk (0, 1, 2, ...)
    data: Blob; // The binary data for this chunk
}

export type ProgressHandler = (
    id: string,
    percent: number,
    bytes: number,
    total: number
) => void;

export class Movies extends Dexie {
    private movies: Table<Movie, string>;
    private chunks: Table<MovieChunk, [string, number]>;

    private activeDownloads = new Map<string, AbortController>();

    constructor() {
        super("Movies");

        // --- SCHEMA CHANGE ---
        // Version bumped to 2 to reflect the new logic of storing the final blob
        // in the 'movies' table after a successful download.
        this.version(2).stores({
            movies: "id, status",
            movie_chunks: "[movieId+order]"
        });

        this.movies = this.table("movies");
        this.chunks = this.table("movie_chunks");
    }

    // --- Helper methods (getUrl, getChecksum) are unchanged ---

    private getUrl(id: string): string {
        const movie = id.split("-");
        const title = movie[0];
        const episode = parseInt(movie[1]) - 1;
        const collection = Collection as any;
        return collection[title].dropbox[episode];
    }

    private getChecksum(id: string): string {
        const movie = id.split("-");
        const title = movie[0];
        const episode = parseInt(movie[1]) - 1;
        const collection = Collection as any;
        return collection[title].checksum[episode];
    }

    // --- Public API methods ---

    async get(id: string): Promise<Movie> {
        const movie = await this.movies.get(id);
        if (movie) return movie;
        else {
            const newMovie = { id, time: 0, downloaded: 0 } as Movie;
            await this.movies.put(newMovie);
            return newMovie;
        }
    }

    async getAllStarted() {
        return this.movies
            .filter((movie) => movie.status === "started")
            .toArray();
    }

    async getTime(id: string): Promise<number> {
        const movie = await this.get(id);
        return movie.time;
    }

    async setTime(id: string, time: number) {
        await this.movies.update(id, { time });
    }

    async remove(id: string) {
        // Use a transaction to ensure both metadata and any leftover chunks are deleted
        await this.transaction("rw", this.movies, this.chunks, async () => {
            await this.movies.delete(id);
            await this.chunks.where({ movieId: id }).delete();
        });
        console.log(`🗑️ Deleted ${id} and all its data.`);
    }

    public pause(id: string) {
        const controller = this.activeDownloads.get(id);
        if (controller) {
            console.log(`⏸️ Pausing download for ${id}...`);
            controller.abort();
        } else {
            console.warn(`No active download found for ${id} to pause.`);
        }
        //this.movies.update(id, { status: "paused" }); already done in download
    }

    /**
     * Downloads a movie by streaming chunks to IndexedDB, then finalizes the download
     * by assembling the chunks, verifying, and storing the final Blob.
     */
    async download(id: string, onProgress?: ProgressHandler) {
        if (this.activeDownloads.has(id)) {
            console.warn(`${id} download already in progress`);
            return;
        }

        let movie = await this.get(id);

        if (movie.status === "completed") {
            console.log(`👍 ${id} is already fully downloaded.`);
            if (movie.totalSize) {
                onProgress?.(id, 100, movie.totalSize, movie.totalSize);
            }
            return;
        }

        const controller = new AbortController();
        this.activeDownloads.set(id, controller);

        try {
            // --- The download-to-chunks part is largely the same ---
            movie.downloaded = movie.downloaded || 0;
            const headers: HeadersInit = {
                Range: `bytes=${movie.downloaded}-`
            };

            const response = await fetch(this.getUrl(id), {
                headers,
                signal: controller.signal
            });

            if (!response.ok || !response.body) {
                if (response.status === 416) {
                    // This can happen if the 'downloaded' value is incorrect. Let's try to finalize.
                    console.log(
                        `👍 ${id} seems to be fully downloaded (server returned 416). Attempting to finalize...`
                    );
                    await this._finalizeDownload(id, onProgress);
                }
                if (response.status === 404) {
                    await this.movies.update(id, { status: "paused" });
                }

                return;
                // throw new Error(
                //     `${id} download failed: ${response.statusText}`
                // );
            }

            let received = movie.downloaded;
            if (response.status !== 206) {
                await this.chunks.where({ movieId: id }).delete();
                received = 0;
                movie.downloaded = 0;
            }

            await this.movies.update(id, { status: "started" });

            if (!movie.totalSize) {
                const contentRange = response.headers.get("Content-Range");
                const totalStr = contentRange
                    ? contentRange.split("/")[1]
                    : "0";
                const contentLength =
                    Number(response.headers.get("Content-Length")) || 0;
                movie.totalSize =
                    parseInt(totalStr, 10) || received + contentLength;
                await this.movies.update(id, { totalSize: movie.totalSize });
            }

            const totalBytes = movie.totalSize || 0;
            const reader = response.body.getReader();
            const lastChunk = await this.chunks.where({ movieId: id }).last();
            let chunkOrder = lastChunk ? lastChunk.order + 1 : 0;
            const CHUNK_BUFFER_SIZE = 5 * 1024 * 1024;
            let buffer: Uint8Array[] = [];
            let bufferSize = 0;
            let lastProgressUpdate = performance.now();

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                if (value) {
                    buffer.push(value);
                    bufferSize += value.length;
                    received += value.length;

                    if (bufferSize >= CHUNK_BUFFER_SIZE) {
                        const blobChunk = new Blob(buffer as BlobPart[]);
                        await this.chunks.put({
                            movieId: id,
                            order: chunkOrder++,
                            data: blobChunk
                        });
                        await this.movies.update(id, { downloaded: received });
                        buffer = [];
                        bufferSize = 0;
                    }

                    const now = performance.now();
                    if (onProgress && now - lastProgressUpdate > 250) {
                        const percent = totalBytes
                            ? Math.min(
                                  100,
                                  Math.round((received / totalBytes) * 100)
                              )
                            : 0;
                        onProgress(id, percent, received, totalBytes);
                        lastProgressUpdate = now;
                    }
                }
            }

            if (buffer.length > 0) {
                const blobChunk = new Blob(buffer as BlobPart[]);
                await this.chunks.put({
                    movieId: id,
                    order: chunkOrder++,
                    data: blobChunk
                });
            }
            await this.movies.update(id, { downloaded: received });

            // --- NEW: Finalization Step ---
            // All bytes are received. Now assemble, verify, and clean up.
            await this._finalizeDownload(id, onProgress);
        } catch (error) {
            if (error instanceof DOMException && error.name === "AbortError") {
                // This is a manual pause, not a network error.
                console.log(`Download for ${id} was paused manually.`);
                await this.movies.update(id, { status: "paused" });
            }
            // --- THIS IS THE NEW LOGIC ---
            else if (
                error instanceof TypeError &&
                error.message === "Load failed"
            ) {
                // A TypeError during fetch while the browser is offline is a strong
                // indicator of a network connection loss.
                console.warn(
                    `Network connection lost for ${id}. Pausing download.`
                );
                //await this.movies.update(id, { status: "paused" }); keep status as started to resume
            }
            // --- END OF NEW LOGIC ---
            else {
                // For all other errors, mark as failed.
                await this.movies.update(id, { status: "failed" });
                console.error(`Download for ${id} failed:`, error);
                //alert(error);
            }
        } finally {
            this.activeDownloads.delete(id);
        }
    }

    // --- Private Helper Methods ---

    /**
     * Assembles chunks from the database into a single Blob.
     * This is a memory-intensive operation.
     */
    private async _getBlobFromChunks(id: string): Promise<Blob | null> {
        const movieChunks = await this.chunks
            .where({ movieId: id })
            .sortBy("order");
        if (movieChunks.length === 0) return null;
        const blobParts = movieChunks.map((chunk) => chunk.data);
        return new Blob(blobParts, { type: "video/mp4" });
    }

    /**
     * The final step after all bytes are downloaded. Assembles the chunks,
     * verifies the checksum, saves the final blob, and cleans up the chunks table.
     */
    private async _finalizeDownload(id: string, onProgress?: ProgressHandler) {
        console.log(`Finalizing download for ${id}. Assembling chunks...`);
        let fullBlob = await this._getBlobFromChunks(id);

        if (!fullBlob) {
            throw new Error(
                "Finalization failed: Could not assemble blob from chunks."
            );
        }

        //if (isDesktop) {
        console.log(`Verifying checksum for ${id}...`);
        const checksum = await this.streamingXXH3(fullBlob); //this._computeSHA256(fullBlob);
        const expectedChecksum = this.getChecksum(id);

        if (checksum !== expectedChecksum) {
            console.error(`❌ ${id} checksum mismatch! Download is corrupted.`);
            console.error(`   Expected: ${expectedChecksum}`);
            console.error(`   Actual:   ${checksum}`);
            //await this.movies.update(id, { status: "failed" }); // already done in catch
            // Optionally, clear the bad chunks
            // await this.chunks.where({ movieId: id }).delete();
            throw new Error("Checksum verification failed.");
        }

        console.log(
            `✅ ${id} checksum verified. Saving final blob and cleaning up chunks.`
        );
        //}

        // if (isDesktop) {
        //     // --- ⬇️ THE NEW, MORE POWERFUL DEEP COPY METHOD ⬇️ ---
        //     //
        //     // To defeat Safari's persistent resource caching, we force a full deep copy
        //     // by converting the blob to an ArrayBuffer and then creating a new blob.
        //     // This guarantees a new, untainted resource in memory.
        //     //
        //     console.log(
        //         `Performing deep copy of blob via ArrayBuffer for Safari...`
        //     );
        //     const buffer = await fullBlob.arrayBuffer();
        //     fullBlob = new Blob([buffer], { type: fullBlob.type });
        //     //console.log(`Deep copy complete. New blob size: ${finalBlob.size}`);
        //     // --- ⬆️ END OF THE NEW METHOD ⬆️ ---
        // }

        // Use a transaction to make the final update atomic
        await this.transaction("rw", this.movies, this.chunks, async () => {
            // 1. Update the movie record with the final blob and completed status
            await this.movies.put({
                id,
                blob: fullBlob,
                status: "completed",
                size: fullBlob.size,
                totalSize: fullBlob.size, // Ensure totalSize matches final size
                downloaded: fullBlob.size,
                time: (await this.get(id)).time // Preserve existing time
            });

            // 2. Delete all chunks for this movie
            await this.chunks.where({ movieId: id }).delete();
        });

        console.info(`✅ ${id} successfully completed and finalized.`);
        onProgress?.(id, 100, fullBlob.size, fullBlob.size);
    }

    /**
     * Computes the XXH3 (64-bit) hash of a Blob in a streaming fashion.
     * This is memory-efficient for large blobs (e.g., GB-scale files) as it processes chunks without loading the entire content.
     *
     * @param blob The Blob to hash (e.g., from a file input).
     * @param chunkSize Optional chunk size in bytes (default: 64MB for balance between memory and performance). Note: This param is not directly used here as Blob.stream() handles chunking internally, but included for consistency with prior examples.
     * @returns A promise resolving to the 16-character hex string of the XXH3 hash (lowercase, zero-padded if needed).
     *
     * Requirements:
     * - Install via: npm install xxhash-wasm
     * - Works in browser or Node.js (with Blob polyfill if needed).
     *
     * Example usage:
     * const fileInput = document.querySelector('input[type="file"]');
     * fileInput.addEventListener('change', async (e) => {
     *   const blob = e.target.files[0];
     *   const hash = await streamingXXH3(blob);
     *   console.log(hash); // e.g., '502b0c5fc4a5704c'
     * });
     */
    private async streamingXXH3(
        blob: Blob,
        chunkSize: number = 1024 * 1024 * 64
    ): Promise<string> {
        // Load XXHash WASM module (async init, can be cached globally if called often)
        const { create64 } = await xxhash();

        // Create a 64-bit XXH3 streaming hasher
        const hasher = create64();

        // Get a readable stream from the Blob
        const stream = blob.stream();

        // Create a reader for the stream
        const reader = stream.getReader();

        try {
            while (true) {
                const { done, value } = await reader.read();

                if (done) {
                    break;
                }

                // Update the hasher with the chunk (Uint8Array)
                hasher.update(value);
            }

            // Compute the digest as bigint and convert to 16-char hex string (lowercase, zero-padded)
            const digestBigInt = hasher.digest();
            return digestBigInt.toString(16).padStart(16, "0");
        } finally {
            // Release the reader to free resources
            reader.releaseLock();
        }
    }
}
